<template>
  <div class="card-analyze-params">
    <!-- 検索期間 -->
    <b-row no-gutters class="section">
      <label class="label" v-t="'appAnalyzeParamsSelector.searchPeriod'" />
      <span class="text">
        {{ periodText }}
      </span>
    </b-row>
    <!-- 対象ユーザー -->
    <b-row no-gutters class="section">
      <label class="label" v-t="'appAnalyzeParamsSelector.targetUsers'" />
      <p class="users" v-bind:class="{ 'is-collapsed': isCollapsed }">
        <span class="count">
          {{ $tc('appCardAnalyzeParams.userCount', userCount) }}
        </span>
        <span class="names" ref="userNames">
          <span
            v-for="(user, index) in usersForDisplay"
            v-bind:key="user.key"
            class="name"
          >
            <!-- 組織 -->
            <text-organization
              v-if="user.type === 'organization'"
              v-bind="user"
              class="text"
            />
            <!-- ユーザー -->
            <span
              v-else-if="user.type === 'user'"
              class="text"
              v-text="user.name"
            />
            <!-- カンマ -->
            <span
              v-if="index !== usersForDisplay.length - 1"
              class="trailing-comma"
              v-text="','"
            />
          </span>
        </span>
      </p>
      <!-- 折りたたみボタン -->
      <b-button
        v-if="shouldShowToggleButton"
        variant="light"
        class="collapse-button"
        v-on:click="isCollapsed = !isCollapsed"
      >
        <icon-collapse v-if="isCollapsed" class="icon" />
        <icon-expand v-else class="icon" />
        <span>
          {{
            isCollapsed
              ? $t('appCardAnalyzeParams.expandButton')
              : $t('appCardAnalyzeParams.collapseButton')
          }}</span
        >
      </b-button>
    </b-row>
    <!-- 表示エリア -->
    <b-row no-gutters v-if="existsIndoorBuilding" class="section">
      <label class="label" v-t="'appAnalyzeParamsSelector.targetArea'" />
      <span class="text">
        {{ areaText }}
      </span>
    </b-row>
  </div>
</template>

<script>
import moment from 'moment'
import { DATE_FORMAT_SLASH, TIME_FORMAT_MINUTE } from '@/constants.js'
import IconCollapse from '@/assets/ifield/icon_collapse.svg?component'
import IconExpand from '@/assets/ifield/icon_expand.svg?component'

// 日時表示用フォーマット
const DATETIME_FORMAT = `${DATE_FORMAT_SLASH} ${TIME_FORMAT_MINUTE}`

// ユーザーリストを折りたたむ行数の閾値
const COLLAPSE_LINE_THRESHOLD = 2

export default {
  name: 'AppCardAnalyzeParams',
  components: {
    'icon-collapse': IconCollapse,
    'icon-expand': IconExpand,
  },
  props: {
    /**
     * 開始日時
     */
    from: {
      type: String,
      default: null,
    },
    /**
     * 終了日時
     */
    to: {
      type: String,
      default: null,
    },
    /**
     * 対象ユーザー
     */
    users: {
      type: Array,
      default: null,
    },
    /**
     * 屋内ビル
     */
    indoorBuildings: {
      type: Array,
      default: null,
    },
  },
  /**
   * デフォルトの折りたたみボタンの状態
   */
  data() {
    return {
      isCollapsed: true,
      shouldShowToggleButton: false,
    }
  },
  computed: {
    /**
     * 期間テキスト
     * @type {String}
     */
    periodText: function () {
      return `${moment(this.from).format(DATETIME_FORMAT)} 〜 ${moment(
        this.to
      ).format(DATETIME_FORMAT)}`
    },
    /**
     * ユーザー数
     * @type {number}
     */
    userCount: function () {
      if (!this.users) return 0
      const userIdSet = new Set()
      for (const { type, id, users } of this.users) {
        if (type === 'user') {
          userIdSet.add(id)
        } else if (type === 'organization') {
          users.forEach((u) => userIdSet.add(u.id))
        }
      }
      return userIdSet.size
    },
    /**
     * 表示用ユーザーデータ
     * @type {Object[]}
     */
    usersForDisplay: function () {
      return this.users.map((u) => ({
        key: `${u.type}_${u.id}`,
        ...u,
      }))
    },
    /**
     * ビルが指定されている場合はtrue
     * @type {Boolean}
     */
    existsIndoorBuilding: function () {
      return this.indoorBuildings && !!this.indoorBuildings.length
    },
    /**
     * 表示エリアテキスト
     * @type {String}
     */
    areaText: function () {
      if (!this.existsIndoorBuilding) return ''
      const building = this.indoorBuildings[0]
      const siteName =
        building.indoor_sites &&
        building.indoor_sites.length &&
        building.indoor_sites[0].name
      if (siteName) {
        return `${siteName}(${building.name})`
      } else {
        return building.name
      }
    },
  },
  /**
   * オーバーフローロジックを更新します
   */
  watch: {
    usersForDisplay: {
      handler() {
        this.$nextTick(() => {
          this.checkOverflow()
        })
      },
      immediate: true,
    },
  },
  mounted() {
    // 初期表示時にチェック
    this.checkOverflow()
    // ウィンドウリサイズイベントを監視
    window.addEventListener('resize', this.checkOverflow)
  },
  beforeDestroy() {
    // コンポーネント破棄時にイベントリスナーを解除
    window.removeEventListener('resize', this.checkOverflow)
  },
  methods: {
    /**
     * ユーザーリストの高さ計算ツール
     */
    checkOverflow() {
      // $nextTick を使用して、DOMが完全にレンダリングされた後にチェック
      this.$nextTick(() => {
        const userList = this.$refs.userNames
        if (!userList) return

        // 一時的に isCollapsed を false にして、コンテンツの完全な高さを取得
        this.isCollapsed = false

        this.$nextTick(() => {
          const fullHeight = userList.scrollHeight
          const style = window.getComputedStyle(userList)
          let lineHeight = parseFloat(style.lineHeight)

          // lineHeight が 取得できなかった場合のフォールバック
          if (isNaN(lineHeight) || lineHeight === 0) {
            const fontSize = parseFloat(style.fontSize)
            lineHeight = fontSize * 1.5
          }

          const maxHeight = lineHeight * COLLAPSE_LINE_THRESHOLD
          // scrollHeightの計算誤差を吸収するための1pxのバッファ
          const overflowDetected = fullHeight > maxHeight + 1

          // 判定結果に応じて、ボタンの表示とリストの折りたたみ状態を設定
          this.shouldShowToggleButton = overflowDetected
          this.isCollapsed = overflowDetected
        })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
$background-color: #eaecef;

.card-analyze-params {
  width: 100%;
  color: $text-color;
  background-color: #eaecef;
  padding: 16px;
  font-size: $font-size-middle;
  line-height: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 16px;
}

.section {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 4px;
}

.label {
  font-weight: $font-weight-bold;
  padding: 0;
  margin-bottom: 0;
}

.text {
  text-align: left;
  line-height: 1.5;
}
.users {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  text-align: left;
  margin: 0;

  .count {
    flex-shrink: 0;
    text-align: left;
    line-height: 1.5;

    &::after {
      content: ':';
      margin-right: calc(#{$font-size-middle} / 4); // 半角スペースの代わり
    }
  }
  .names {
    display: block;
    line-height: 1.5;
    .name {
      display: inline-block;

      .trailing-comma {
        margin-right: calc(#{$font-size-middle} / 4); // 半角スペースの代わり
      }
    }
  }
}

.users.is-collapsed .names {
  /* 2行分の高さに制限 (line-height: 1.5 * 2) */
  max-height: 3em;
  overflow: hidden;
  mask-image: linear-gradient(
    to bottom,
    $background-color,
    #{$background-color}99,
    #{$background-color}33
  );
}

.collapse-button {
  background-color: transparent;
  border: 1px solid #ced4da;
  border-radius: 4px;
  color: $text-color;
  width: 124px;
  height: 32px;
  margin-left: auto;
  margin-right: auto;
  padding: 6px 16px;
  display: flex;
  align-items: center;
  font-size: $font-size-middle;

  .icon {
    margin-right: 8px;
  }
}
</style>
